// src/features/operational/components/teaching/LessonForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  Chip,
  IconButton,
  Paper
} from '@mui/material';
import { 
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';
import { 
  fetchSubjectPlans, 
  fetchSubjectResources,
  fetchLearners
} from '../../services/teachingService';

const LessonForm = ({ open, onClose, onSubmit, subjects = [], initialData = null }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [resources, setResources] = useState([]);
  const [learners, setLearners] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    date: formatDate(new Date()),
    time: '08:00',
    term: getCurrentTerm().toString(),
    subject: '',
    grade: '',
    atpItem: '',
    resource: '',
    topic: '',
    lessonPlan: null, // For file upload
    assessmentType: '',
    hasHomework: false,
    homeworkDescription: '',
    expectedAttendance: '',
    actualAttendance: '',
    absentees: [],
    periodRegister: true,
    learnerEngagement: 'Medium',
    lessonProgress: '75',
    atpStatus: 'As planned'
  });
  
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  useEffect(() => {
    // If subject changes, fetch related data
    if (formData.subject) {
      loadSubjectData(formData.subject);
    }
  }, [formData.subject]);
  
  const loadSubjectData = async (subjectId) => {
    setLoading(true);
    try {
      const [plansData, resourcesData, learnersData] = await Promise.all([
        fetchSubjectPlans(currentUser.uid, subjectId),
        fetchSubjectResources(subjectId),
        fetchLearners(formData.grade)
      ]);
      
      setPlans(plansData);
      setResources(resourcesData);
      setLearners(learnersData);
    } catch (error) {
      console.error("Error loading subject data:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({
      date: formatDate(new Date()),
      time: '08:00',
      term: getCurrentTerm().toString(),
      subject: '',
      grade: '',
      atpItem: '',
      resource: '',
      topic: '',
      lessonPlan: null,
      assessmentType: '',
      hasHomework: false,
      homeworkDescription: '',
      expectedAttendance: '',
      actualAttendance: '',
      absentees: [],
      periodRegister: true,
      learnerEngagement: 'Medium',
      lessonProgress: '75',
      atpStatus: 'As planned'
    });
    setPlans([]);
    setResources([]);
    setLearners([]);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Handle grade change from subject selection
    if (name === 'subject') {
      const selectedSubject = subjects.find(s => s.id === value);
      if (selectedSubject) {
        setFormData(prev => ({
          ...prev,
          grade: selectedSubject.grade
        }));
      }
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        lessonPlan: e.target.files[0]
      }));
    }
  };
  
  const handleAbsenteesChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      absentees: newValue
    }));
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(formData);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error submitting lesson:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper functions
  function formatDate(date) {
    return date.toISOString().slice(0, 10);
  }
  
  function getCurrentTerm() {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    
    if (month >= 1 && month <= 3) return 1;
    else if (month >= 4 && month <= 6) return 2;
    else if (month >= 7 && month <= 9) return 3;
    else return 4;
  }
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 1.5 }}>
        <Typography variant="h6" fontWeight="bold">
          {initialData ? 'Edit Lesson' : 'Record New Lesson'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} sx={{ minWidth: 240 }}>
                  <TextField
                    fullWidth
                    required
                    label="Date"
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ minWidth: 240 }}>
                  <TextField
                    fullWidth
                    required
                    label="Time"
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ minWidth: 240 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Term</InputLabel>
                    <Select
                      name="term"
                      value={formData.term}
                      label="Term"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="1">Term 1</MenuItem>
                      <MenuItem value="2">Term 2</MenuItem>
                      <MenuItem value="3">Term 3</MenuItem>
                      <MenuItem value="4">Term 4</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Subject</InputLabel>
                    <Select
                      name="subject"
                      value={formData.subject}
                      label="Subject"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">
                        <em>Select a subject</em>
                      </MenuItem>
                      {subjects.map((subject) => (
                        <MenuItem key={subject.id} value={subject.id}>
                          {subject.name} - Grade {subject.grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <TextField
                    fullWidth
                    required
                    label="Grade"
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                    InputProps={{
                      readOnly: Boolean(formData.subject), // Read-only if subject is selected
                    }}
                    helperText={formData.subject ? "Auto-filled from subject" : ""}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Lesson Content */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                Lesson Content
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Lesson Topic"
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <FormControl fullWidth>
                    <InputLabel>ATP Item Reference</InputLabel>
                    <Select
                      name="atpItem"
                      value={formData.atpItem}
                      label="ATP Item Reference"
                      onChange={handleInputChange}
                      disabled={plans.length === 0}
                    >
                      <MenuItem value="">
                        <em>Select an ATP item</em>
                      </MenuItem>
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <FormControl fullWidth>
                    <InputLabel>Resource Reference</InputLabel>
                    <Select
                      name="resource"
                      value={formData.resource}
                      label="Resource Reference"
                      onChange={handleInputChange}
                      disabled={resources.length === 0}
                    >
                      <MenuItem value="">
                        <em>Select a resource</em>
                      </MenuItem>
                      {resources.map((resource) => (
                        <MenuItem key={resource.id} value={resource.id}>
                          {resource.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      Lesson Plan Document
                    </Typography>
                    {formData.lessonPlan ? (
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <Box display="flex" alignItems="center">
                          {formData.lessonPlan.name?.endsWith('.pdf') ? (
                            <PdfIcon sx={{ color: '#f44336', mr: 1 }} />
                          ) : (
                            <DocIcon sx={{ color: '#2196f3', mr: 1 }} />
                          )}
                          <Typography variant="body2">
                            {formData.lessonPlan.name}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => setFormData(prev => ({ ...prev, lessonPlan: null }))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ) : (
                      <Box>
                        <input
                          accept=".pdf,.doc,.docx"
                          style={{ display: 'none' }}
                          id="lesson-plan-upload"
                          type="file"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="lesson-plan-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            sx={{
                              borderColor: THEME_COLORS.primary,
                              color: THEME_COLORS.primary,
                              '&:hover': {
                                borderColor: THEME_COLORS.primary,
                                backgroundColor: 'rgba(39, 35, 97, 0.04)'
                              }
                            }}
                          >
                            Upload Lesson Plan
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sx={{ minWidth: 240 }}>
                  <FormControl fullWidth>
                    <InputLabel>Assessment Type</InputLabel>
                    <Select
                      name="assessmentType"
                      value={formData.assessmentType}
                      label="Assessment Type"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="Classwork">Classwork</MenuItem>
                      <MenuItem value="Class Quiz">Class Quiz</MenuItem>
                      <MenuItem value="Class Test">Class Test</MenuItem>
                      <MenuItem value="Presentation">Presentation</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hasHomework}
                        onChange={handleSwitchChange}
                        name="hasHomework"
                        color="primary"
                      />
                    }
                    label="Homework Issued"
                  />
                  {formData.hasHomework && (
                    <TextField
                      fullWidth
                      label="Homework Description"
                      name="homeworkDescription"
                      value={formData.homeworkDescription}
                      onChange={handleInputChange}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Attendance & Progress */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                Attendance & Progress
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <TextField
                    fullWidth
                    label="Expected Attendance"
                    name="expectedAttendance"
                    type="number"
                    value={formData.expectedAttendance}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ minWidth: 240 }}>
                  <TextField
                    fullWidth
                    label="Actual Attendance"
                    name="actualAttendance"
                    type="number"
                    value={formData.actualAttendance}
                    onChange={handleInputChange}
                    inputProps={{ min: 0 }}
                    error={formData.actualAttendance > formData.expectedAttendance}
                    helperText={formData.actualAttendance > formData.expectedAttendance ? "Cannot exceed expected attendance" : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.periodRegister}
                        onChange={handleSwitchChange}
                        name="periodRegister"
                        color="primary"
                      />
                    }
                    label="Period Register Completed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={learners}
                    getOptionLabel={(option) => option.name}
                    value={formData.absentees}
                    onChange={handleAbsenteesChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Absentees"
                        placeholder="Select absent learners"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          avatar={<SchoolIcon />}
                          sx={{ backgroundColor: 'rgba(39, 35, 97, 0.08)' }}
                        />
                      ))
                    }
                    disabled={learners.length === 0}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
          </Grid>

          {/* Lesson Effectiveness */}
          <Grid item xs={12}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" color={THEME_COLORS.primary} gutterBottom>
                Lesson Effectiveness
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>Learner Engagement</InputLabel>
                    <Select
                      name="learnerEngagement"
                      value={formData.learnerEngagement}
                      label="Learner Engagement"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Low">Low</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="High">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ minWidth: 200 }}>
                  <TextField
                    fullWidth
                    label="Lesson Progress (%)"
                    name="lessonProgress"
                    type="number"
                    value={formData.lessonProgress}
                    onChange={handleInputChange}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ minWidth: 200 }}>
                  <FormControl fullWidth>
                    <InputLabel>ATP Status</InputLabel>
                    <Select
                      name="atpStatus"
                      value={formData.atpStatus}
                      label="ATP Status"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Ahead">Ahead of Schedule</MenuItem>
                      <MenuItem value="As planned">As Planned</MenuItem>
                      <MenuItem value="Behind">Behind Schedule</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    
    <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button onClick={onClose}>Cancel</Button>
      <Button 
        onClick={handleSubmit}
        variant="contained"
        disabled={loading || !formData.subject || !formData.topic || !formData.date}
        sx={{
          bgcolor: THEME_COLORS.primary,
          '&:hover': {
            bgcolor: '#1e1c4e'
          }
        }}
      >
        {loading ? 'Saving...' : initialData ? 'Update Lesson' : 'Save Lesson'}
      </Button>
    </DialogActions>
  </Dialog>
);
};

export default LessonForm;
